import { useState } from "react";
import {
  Alert,
  App,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import {
  BotBillableStatus,
  BotInterface,
  BotStatus,
  useArchiveBotMutation,
  usePauseBotMutation,
  useResumeBotMutation,
  useUpdateBotMutation,
} from "api";
import { format, dateTimeFormat, getBase64 } from "libs";
import type { FileType } from "pages/admin/UserPage/UserInfo";
import { getErrorMessage, Put } from "components";

interface BotInfoTabProps {
  bot: BotInterface;
}

const STATUS_TONE: Record<BotStatus, "success" | "warning" | "default"> = {
  [BotStatus.ACTIVE]: "success",
  [BotStatus.PAUSED]: "warning",
  [BotStatus.ARCHIVED]: "default",
};

const BILLABLE_TONE: Record<
  BotBillableStatus,
  "success" | "warning" | "error" | "default"
> = {
  [BotBillableStatus.ACTIVE]: "success",
  [BotBillableStatus.GRACE]: "warning",
  [BotBillableStatus.DISABLED_UNPAID]: "error",
  [BotBillableStatus.EXPIRED]: "default",
};

export const BotInfoTab = ({ bot }: BotInfoTabProps) => {
  const { notification, modal } = App.useApp();

  const [editOpen, setEditOpen] = useState(false);
  const [form] = Form.useForm();
  // Avatar preview / payload: seeded with the current picture URL on open;
  // becomes a base64 data string once the admin picks a new file. The backend
  // ignores a URL (unchanged) and uploads a base64 payload.
  const [photo, setPhoto] = useState<string | undefined>(undefined);

  const [updateBot, { isLoading: isSaving }] = useUpdateBotMutation();
  const [pauseBot, { isLoading: isPausing }] = usePauseBotMutation();
  const [resumeBot, { isLoading: isResuming }] = useResumeBotMutation();
  const [archiveBot, { isLoading: isArchiving }] = useArchiveBotMutation();

  // Guard against unexpected API shape — render an actionable error rather
  // than crashing the whole tab. Placed AFTER all hook calls so the hook order
  // is stable across renders (react-hooks/rules-of-hooks).
  if (!bot.status || !bot.billable_status) {
    return (
      <Alert
        type="error"
        showIcon
        message="Datele botului sunt incomplete"
        description="Reincarca pagina (Cmd+Shift+R / Ctrl+Shift+R). Daca eroarea persista, anunta dezvoltatorul."
      />
    );
  }

  const onEdit = () => {
    form.setFieldsValue({
      name: bot.name,
      username: bot.user?.username ?? undefined,
      persona: bot.persona,
    });
    setPhoto(bot.user?.profile_picture ?? undefined);
    setEditOpen(true);
  };

  const onSave = async () => {
    const values = await form.validateFields();
    try {
      await updateBot({
        id: bot.id,
        name: values.name,
        persona: values.persona,
        username: values.username?.trim()
          ? values.username.trim().toLowerCase()
          : undefined,
        profile_picture: photo,
      }).unwrap();
      notification.success({ message: "Modificarile au fost salvate." });
      setEditOpen(false);
    } catch (e) {
      notification.error({ message: getErrorMessage(e as never) });
    }
  };

  const onArchive = () => {
    modal.confirm({
      title: `Arhiveaza botul "${bot.name}"?`,
      content:
        "Botul nu va mai actiona dar ramane platit pana la finalul ciclului curent. Banii nu se returneaza. Decizia este IREVERSIBILA.",
      okText: "Arhiveaza",
      okButtonProps: { danger: true, loading: isArchiving },
      cancelText: "Anuleaza",
      onOk: async () => {
        try {
          await archiveBot({ id: bot.id }).unwrap();
          notification.success({ message: "Bot arhivat." });
        } catch (e) {
          notification.error({ message: getErrorMessage(e as never) });
        }
      },
    });
  };

  const onPauseToggle = async () => {
    try {
      if (bot.status.value === BotStatus.PAUSED) {
        await resumeBot({ id: bot.id }).unwrap();
        notification.success({ message: "Bot reluat." });
      } else {
        await pauseBot({ id: bot.id }).unwrap();
        notification.success({ message: "Bot pauzat." });
      }
    } catch (e) {
      notification.error({ message: getErrorMessage(e as never) });
    }
  };

  const isArchived = bot.status.value === BotStatus.ARCHIVED;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={16}>
        <Card
          title="Identitate"
          extra={
            <Space>
              {!isArchived && (
                <Button
                  loading={isPausing || isResuming}
                  onClick={onPauseToggle}
                >
                  {bot.status.value === BotStatus.PAUSED
                    ? "Reia"
                    : "Pauzeaza"}
                </Button>
              )}
              {!isArchived && <Button onClick={onEdit}>Editeaza</Button>}
              {!isArchived && (
                <Button danger onClick={onArchive}>
                  Arhiveaza
                </Button>
              )}
            </Space>
          }
        >
          <Descriptions column={1} size="small" colon={false}>
            <Descriptions.Item label="Nume intern">{bot.name}</Descriptions.Item>
            <Descriptions.Item label="Nume afisat in feed">
              {bot.persona.display_first_name} {bot.persona.display_last_name}
            </Descriptions.Item>
            <Descriptions.Item label="Tip">
              {bot.persona.user_type === 1 ? "Persoana fizica (PF)" : "Persoana juridica (PJ)"}
            </Descriptions.Item>
            <Descriptions.Item label="Bio">
              <Put>{bot.persona.bio}</Put>
            </Descriptions.Item>
            <Descriptions.Item label="Prompt LLM (fragment)">
              <Typography.Paragraph
                copyable
                style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}
              >
                {bot.persona.system_prompt_fragment}
              </Typography.Paragraph>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Localizare" style={{ marginTop: 16 }}>
          <Descriptions column={1} size="small" colon={false}>
            <Descriptions.Item label="Tara">
              <Put>{bot.location?.country?.name}</Put>
            </Descriptions.Item>
            <Descriptions.Item label="Judet">
              <Put>{bot.location?.county?.name}</Put>
            </Descriptions.Item>
            <Descriptions.Item label="Oras">
              <Put>{bot.location?.city?.name}</Put>
            </Descriptions.Item>
            <Descriptions.Item label="Categorii">
              <Put condition={bot.categories.length > 0}>
                <Space wrap size={[4, 4]}>
                  {bot.categories.map((c) => (
                    <Tag key={c.id}>{c.name}</Tag>
                  ))}
                </Space>
              </Put>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>

      <Col xs={24} md={8}>
        <Card title="Stare">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <Typography.Text type="secondary">
                Status admin
              </Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Badge
                  status={STATUS_TONE[bot.status.value]}
                  text={bot.status.label}
                />
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">Status plata</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Badge
                  status={BILLABLE_TONE[bot.billable_status.value]}
                  text={bot.billable_status.label}
                />
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">Operational</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Badge
                  status={bot.is_operational ? "processing" : "default"}
                  text={bot.is_operational ? "Da" : "Nu"}
                />
              </div>
            </div>
          </Space>
        </Card>

        <Card title="Cont sporapi" style={{ marginTop: 16 }}>
          <Descriptions column={1} size="small" colon={false}>
            <Descriptions.Item label="user_id">
              {bot.user?.id}
            </Descriptions.Item>
            <Descriptions.Item label="email">
              <Typography.Text copyable>{bot.user?.email}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="username">
              <Put>
                {bot.user?.username ? `@${bot.user.username}` : null}
              </Put>
            </Descriptions.Item>
            <Descriptions.Item label="Poza profil">
              <Avatar
                src={bot.user?.profile_picture ?? undefined}
                size={48}
                icon={<UserOutlined />}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Ultima activitate">
              <Put>
                {bot.user?.last_activity
                  ? format(bot.user.last_activity, dateTimeFormat)
                  : null}
              </Put>
            </Descriptions.Item>
            <Descriptions.Item label="Activat la">
              <Put>
                {bot.activated_at
                  ? format(bot.activated_at, dateTimeFormat)
                  : null}
              </Put>
            </Descriptions.Item>
            <Descriptions.Item label="Dezactivat la">
              <Put>
                {bot.disabled_at
                  ? format(bot.disabled_at, dateTimeFormat)
                  : null}
              </Put>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>

      <Modal
        title="Editeaza bot"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={onSave}
        okText="Salveaza"
        cancelText="Anuleaza"
        confirmLoading={isSaving}
        width={620}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Nume intern"
            rules={[{ required: true }]}
          >
            <Input maxLength={100} />
          </Form.Item>

          <Form.Item label="Poza de profil">
            <Upload
              accept="image/*"
              showUploadList={false}
              customRequest={({ file }) =>
                getBase64(file as FileType, (url) => setPhoto(url))
              }
            >
              <Avatar
                src={photo}
                size={72}
                icon={<UserOutlined />}
                style={{ cursor: "pointer" }}
              />
            </Upload>
            <Typography.Text
              type="secondary"
              style={{ display: "block", fontSize: 12, marginTop: 4 }}
            >
              Apasa pe avatar ca sa incarci o poza noua.
            </Typography.Text>
          </Form.Item>

          <Form.Item
            name="username"
            label="Username"
            rules={[
              { min: 4, message: "Minim 4 caractere" },
              { max: 20, message: "Maxim 20 caractere" },
              {
                pattern: /^[a-z0-9_]+$/,
                message: "Doar litere mici, cifre si _",
              },
            ]}
            extra="Trebuie sa fie unic. Se verifica la salvare."
          >
            <Input addonBefore="@" maxLength={20} />
          </Form.Item>

          <Form.Item
            name={["persona", "display_first_name"]}
            label="Prenume afisat"
            rules={[{ required: true }]}
          >
            <Input maxLength={50} />
          </Form.Item>
          <Form.Item
            name={["persona", "display_last_name"]}
            label="Nume afisat"
            rules={[{ required: true }]}
          >
            <Input maxLength={50} />
          </Form.Item>
          <Form.Item name={["persona", "user_type"]} label="Tip">
            <Select
              options={[
                { value: 1, label: "Persoana fizica (PF)" },
                { value: 2, label: "Persoana juridica (PJ)" },
              ]}
            />
          </Form.Item>
          <Form.Item name={["persona", "bio"]} label="Bio">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          <Form.Item
            name={["persona", "system_prompt_fragment"]}
            label="Fragment prompt LLM"
            rules={[{ required: true, min: 20, max: 400 }]}
          >
            <Input.TextArea rows={3} maxLength={400} />
          </Form.Item>
        </Form>
      </Modal>
    </Row>
  );
};
